import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { value: "+1", label: "+1 (US)" },
  { value: "+44", label: "+44 (UK)" },
  { value: "+91", label: "+91 (IN)" },
  { value: "+61", label: "+61 (AU)" },
  { value: "+86", label: "+86 (CN)" },
  { value: "+81", label: "+81 (JP)" },
  { value: "+49", label: "+49 (DE)" },
  { value: "+33", label: "+33 (FR)" },
  { value: "+39", label: "+39 (IT)" },
  { value: "+55", label: "+55 (BR)" },
  { value: "+52", label: "+52 (MX)" },
  { value: "+34", label: "+34 (ES)" },
  { value: "+82", label: "+82 (KR)" },
  { value: "+971", label: "+971 (AE)" },
  { value: "+65", label: "+65 (SG)" },
  { value: "+7", label: "+7 (RU)" },
  { value: "+27", label: "+27 (ZA)" },
  { value: "+234", label: "+234 (NG)" },
  { value: "+63", label: "+63 (PH)" },
  { value: "+66", label: "+66 (TH)" },
];

export default function LeadFormDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [contactNumber, setContactNumber] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setName("");
    setEmail("");
    setCountryCode("+1");
    setContactNumber("");
    setMessage("");
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setContactNumber(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !contactNumber.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (contactNumber.length < 7) {
      toast.error("Please enter a valid phone number (min 7 digits).");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        country_code: countryCode,
        contact_number: contactNumber,
        message: message.trim(),
      });
      toast.success("Thank you! We'll be in touch shortly.");
      resetForm();
      onOpenChange(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] p-0 overflow-hidden border-0 rounded-xl max-h-[92vh] overflow-y-auto"
        data-testid="lead-form-dialog"
      >
        {/* Header */}
        <div className="bg-[#1E3A8A] px-4 pt-3 pb-3 sm:px-6 sm:pt-6 sm:pb-5 text-white">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1 sm:mb-2">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] opacity-80">
                Trademark Officers
              </span>
            </div>
            <DialogTitle className="text-base sm:text-xl font-heading font-extrabold text-white">
              Protect Your Brand Today
            </DialogTitle>
            <DialogDescription className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Fill in your details and our experts will get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-3 sm:p-6 sm:space-y-4" data-testid="lead-form">
          <div className="space-y-1">
            <Label htmlFor="lead-name" className="text-xs sm:text-sm font-medium text-slate-700">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lead-name"
              data-testid="lead-form-name"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="lead-email" className="text-xs sm:text-sm font-medium text-slate-700">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lead-email"
              data-testid="lead-form-email"
              type="email"
              placeholder="john@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs sm:text-sm font-medium text-slate-700">
              Contact Number <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger
                  className="w-[120px] shrink-0"
                  data-testid="lead-form-country-code"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map((cc) => (
                    <SelectItem key={cc.value} value={cc.value}>
                      {cc.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                data-testid="lead-form-phone"
                placeholder="1234567890"
                value={contactNumber}
                onChange={handlePhoneChange}
                inputMode="numeric"
                maxLength={10}
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="lead-message" className="text-xs sm:text-sm font-medium text-slate-700">
              Message <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="lead-message"
              data-testid="lead-form-message"
              placeholder="Tell us about your brand or trademark needs..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="sm:min-h-[80px]"
            />
          </div>

          <Button
            type="submit"
            data-testid="lead-form-submit"
            className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10 sm:h-11 text-sm font-semibold hover:scale-[1.01] active:scale-[0.99] transition-all"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Register My Trademark"
            )}
          </Button>

          <p className="text-[10px] sm:text-xs text-center text-slate-400 pt-0.5 sm:pt-1">
            By submitting, you agree to our Privacy Policy and Terms of Service.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
